import { action, makeObservable, observable } from 'mobx'

import SharedStore from 'shared/stores/shared.store'

import {
  createCompaniesEntityApi,
  CompaniesEntityApi,
  CompanyResponse,
  CompanySessionResponse,
  AddressResponse,
  RepresentativeReqData,
  AddressReqData,
  CompanyEntityReqData,
  TaxReqData,
} from '../api/companiesEntity.api'

type EntityWithAddress = CompanyResponse & AddressResponse

export class CompaniesEntityStore extends SharedStore<CompaniesEntityApi> {
  storeName = 'CompaniesEntityStore'
  declare childApi: CompaniesEntityApi

  @observable entity: EntityWithAddress = null
  @observable verificationSession: CompanySessionResponse = null

  constructor(root) {
    super()
    this.childApi = createCompaniesEntityApi(root.apiClient)

    makeObservable(this)
  }

  @action setEntity = (entity: EntityWithAddress) => {
    this.entity = entity
  }

  @action setVerificationSession = (session: CompanySessionResponse) => {
    this.verificationSession = session
  }

  @action resetVerificationSession = () => {
    this.verificationSession = null
  }

  createEntity = async (data?: CompanyEntityReqData) => {
    this.toggleLoading(true)

    const response = await this.childApi.createEntity(data)

    if (response.responseType === 'success') {
      this.setEntity(response.data)
    }

    this.toggleLoading(false)

    return response
  }

  fetchEntity = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.fetchEntity(id)

    if (response.responseType === 'success') {
      const existingAddress = response.included?.[0]?.attributes

      this.setEntity({
        ...response.data,
        ...existingAddress,
      })
    }

    this.toggleLoading(false)

    return response
  }

  updateRepresentative = async (id: string, data: RepresentativeReqData) =>
    await this.childApi.updateRepresentative(id, data)

  updateEntity = async (id: string, data: CompanyEntityReqData) => await this.childApi.updateEntity(id, data)

  updateAddress = async (id: string, data: AddressReqData) => await this.childApi.updateAddress(id, data)

  updateTax = async (id: string, data: TaxReqData) => await this.childApi.updateTax(id, data)

  getVerificationSession = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.getCurrentSession(id)

    if (response.responseType === 'success') {
      this.setVerificationSession(response.data)
    }

    this.toggleLoading(false)

    return response
  }

  createSession = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.createSession(id)

    if (response.responseType === 'success') {
      this.setVerificationSession(response.data)
    }

    this.toggleLoading(false)

    return response
  }
}
